/**
 * Paginacao (spec 058; Vitor, 20/10: "depois de 20 registros de tamanho, vamos aplicar uma paginação, se não a rolagem
 * fica muito grande"). De 20 em 20, com os numeros das paginas: "21 a 40 de 137", Anterior, 1 2 3 ... 7, Próxima. No
 * celular, so "Página 2 de 7" com Anterior e Próxima. Com 20 ou menos, nao aparece.
 *
 * So desenha: quem usa guarda a pagina (na URL, pelo hook do app) e diz o total. A `Tabela` e a `Planilha` ja trazem
 * ela pela prop `paginacao`; lista de cartoes usa `paginar` e poe a `Paginacao` embaixo.
 */
import { IconeSeta, IconeVoltar } from "../icones";

export const POR_PAGINA = 20;

/**
 * O que a `Tabela` e a `Planilha` recebem pra paginar. Sem `total`, paginam a lista inteira que receberam (ela ja chegou
 * toda do servidor); com `total`, a lista ja e a pagina que o servidor mandou.
 */
export type PaginacaoDaLista = { pagina: number; aoMudar: (pagina: number) => void; total?: number; rotulo?: string };

export const totalDePaginas = (total: number, porPagina = POR_PAGINA) => Math.max(1, Math.ceil(total / porPagina));

/** A pagina dentro do que existe: depois de um filtro que encolhe a lista, a pagina 7 vira a ultima. */
export const paginaValida = (pagina: number, total: number, porPagina = POR_PAGINA) => Math.min(Math.max(1, Math.floor(pagina) || 1), totalDePaginas(total, porPagina));

/** Os itens da pagina, quando a lista inteira esta na tela. */
export function paginar<T>(itens: readonly T[], pagina: number, porPagina = POR_PAGINA): T[] {
  const p = paginaValida(pagina, itens.length, porPagina);
  return itens.slice((p - 1) * porPagina, p * porPagina);
}

/** Os numeros que aparecem: a primeira, a ultima e as vizinhas da atual, com "..." no buraco (1 ... 4 5 6 ... 12). */
export function numerosDasPaginas(atual: number, paginas: number): (number | "...")[] {
  if (paginas <= 7) return Array.from({ length: paginas }, (_, i) => i + 1);
  const perto = new Set([1, paginas, atual - 1, atual, atual + 1].filter((n) => n >= 1 && n <= paginas));
  // Perto das pontas mostra uma a mais, pra nao ficar "1 ... 3".
  if (atual <= 3) [2, 3, 4].forEach((n) => perto.add(n));
  if (atual >= paginas - 2) [paginas - 1, paginas - 2, paginas - 3].forEach((n) => perto.add(n));
  const ordem = [...perto].sort((a, b) => a - b);
  return ordem.flatMap((n, i) => (i > 0 && n - ordem[i - 1]! > 1 ? (["...", n] as const) : ([n] as const)));
}

export function Paginacao({ pagina, total, aoMudar, porPagina = POR_PAGINA, rotulo = "Páginas" }: { pagina: number; total: number; aoMudar: (pagina: number) => void; porPagina?: number; rotulo?: string }) {
  if (total <= porPagina) return null;
  const paginas = totalDePaginas(total, porPagina);
  const atual = paginaValida(pagina, total, porPagina);
  const de = (atual - 1) * porPagina + 1;
  const ate = Math.min(atual * porPagina, total);
  const ir = (p: number) => { if (p !== atual && p >= 1 && p <= paginas) aoMudar(p); };
  return (
    <nav className="mtc-paginacao" aria-label={rotulo}>
      <span className="mtc-paginacao__faixa">{de.toLocaleString("pt-BR")} a {ate.toLocaleString("pt-BR")} de {total.toLocaleString("pt-BR")}</span>
      {/* So uma das duas aparece (CSS): a faixa no desktop, "Página 2 de 7" no celular; a escondida sai do leitor de tela junto. */}
      <span className="mtc-paginacao__curta">Página {atual} de {paginas}</span>
      <div className="mtc-paginacao__botoes">
        <button type="button" className="mtc-paginacao__passo" onClick={() => ir(atual - 1)} disabled={atual === 1} aria-label="Página anterior"><IconeVoltar aria-hidden="true" /><span>Anterior</span></button>
        <ol className="mtc-paginacao__numeros">
          {numerosDasPaginas(atual, paginas).map((n, i) =>
            n === "..." ? (
              <li key={`buraco-${i}`} className="mtc-paginacao__buraco" aria-hidden="true">...</li>
            ) : (
              <li key={n}>
                <button type="button" className="mtc-paginacao__numero" aria-current={n === atual ? "page" : undefined} aria-label={`Página ${n}`} onClick={() => ir(n)}>{n}</button>
              </li>
            ),
          )}
        </ol>
        <button type="button" className="mtc-paginacao__passo" onClick={() => ir(atual + 1)} disabled={atual === paginas} aria-label="Próxima página"><span>Próxima</span><IconeSeta aria-hidden="true" /></button>
      </div>
    </nav>
  );
}
